// How students actually type school names — ONE matching rule, mirrored from
// the KB server matcher (firestore_db._matches_school): normalized substring,
// the name's acronym ("ucla", "byu", "csuf"), or a system-prefix expansion
// ("csu fullerton" → "california state university fullerton"). Founder report
// 2026-07-30: abbreviation queries found nothing on either search surface.

const QUERY_PREFIXES = {
    'uc': 'university of california',
    'csu': 'california state university',
    'cal state': 'california state university',
    'cal poly': 'california polytechnic',
    'suny': 'state university of new york',
    'unc': 'university of north carolina',
};
const ACRONYM_STOPWORDS = new Set(['of', 'at', 'the', 'and', 'in', 'a']);

export function normSchoolText(text) {
    return String(text || '')
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')      // strip diacritics — José → Jose
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
}

export function schoolAcronym(officialName) {
    return normSchoolText(officialName)
        .split(' ')
        .filter((w) => w && !ACRONYM_STOPWORDS.has(w))
        .map((w) => w[0])
        .join('');
}

export function matchesSchool({ name, id }, query) {
    const n = normSchoolText(name);
    const uid = normSchoolText(String(id || '').replace(/[_-]/g, ' '));
    const q = normSchoolText(query);
    if (!q || !(n || uid)) return false;
    if (n.includes(q) || uid.includes(q)) return true;
    const compact = q.replace(/ /g, '');
    if (compact && compact === schoolAcronym(name)) return true;
    for (const [prefix, expansion] of Object.entries(QUERY_PREFIXES)) {
        if (q === prefix || q.startsWith(`${prefix} `)) {
            const expanded = (expansion + q.slice(prefix.length)).trim();
            if (n.includes(expanded)) return true;
        }
    }
    const terms = q.split(' ');
    return terms.length > 1 && terms.every((t) => n.includes(t));
}

/**
 * The roster school a display name means (#5229: the chat's nudge presets
 * the free read by the name the answer spoke, since the answer carries no
 * id). The names are two spellings of one knowledge base (the page's name
 * against the directory's), so: the one whose normalized name is equal,
 * else the ONE whose name contains the other's, else null. Two candidates
 * is no answer: never a guessed school (product invariant 5).
 */
export function schoolByName(roster, name) {
    const q = normSchoolText(name);
    if (!q) return null;
    const list = (roster || []).filter((s) => s && s.id && s.name);
    const exact = list.find((s) => normSchoolText(s.name) === q);
    if (exact) return exact;
    const near = list.filter((s) => {
        const n = normSchoolText(s.name);
        return n.includes(q) || q.includes(n);
    });
    return near.length === 1 ? near[0] : null;
}
